import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { isEmail, isLink } from 'shared/lib'
import { CountryEnum, PhoneInputStore } from 'shared/ui'
import { type INormalizedApiError } from 'shared/api'
import { type IResponseRcs } from 'entities/Rcs'
import { type IRcsEmail, type IRcsNumber, type IRcsWebsite } from './type'

const MAX_CONTACTS = 3

export class BrandContactsStore {
  private _numbers: Map<string, IRcsNumber> = new Map()
  private _websites: Map<string, IRcsWebsite> = new Map()
  private _emails: Map<string, IRcsEmail> = new Map()

  constructor() {
    this.addNumber()

    makeAutoObservable(this)
  }

  get numbers() {
    return Array.from(this._numbers.values())
  }

  get websites() {
    return Array.from(this._websites.values())
  }

  get emails() {
    return Array.from(this._emails.values())
  }

  get hasNumbers() {
    return this._numbers.size > 0
  }

  get hasWebsites() {
    return this._websites.size > 0
  }

  get hasEmails() {
    return this._emails.size > 0
  }

  get canAddNumber() {
    return this._numbers.size < MAX_CONTACTS
  }

  get canAddWebsite() {
    return this._websites.size < MAX_CONTACTS
  }

  get canAddEmail() {
    return this._emails.size < MAX_CONTACTS
  }

  get canDeleteNumberOrEmail() {
    return (
      (!!this._numbers.size && !!this._emails.size) ||
      this._numbers.size > 1 ||
      this._emails.size > 1
    )
  }

  addNumber = () => {
    if (!this.canAddNumber) return

    const number: IRcsNumber = {
      id: nanoid(),
      label: '',
      labelError: '',
      phoneInputStore: new PhoneInputStore({ excludeCountries: [CountryEnum.GB, CountryEnum.AU] }),
    }

    this._numbers.set(number.id, number)
  }

  addWebsite = () => {
    if (!this.canAddWebsite) return

    const website: IRcsWebsite = {
      id: nanoid(),
      url: '',
      urlError: '',
      label: '',
      labelError: '',
    }

    this._websites.set(website.id, website)
  }

  addEmail = () => {
    if (!this.canAddEmail) return

    const email: IRcsEmail = {
      id: nanoid(),
      email: '',
      emailError: '',
      label: '',
      labelError: '',
    }

    this._emails.set(email.id, email)
  }

  removeNumber = (id: string) => {
    this._numbers.delete(id)
  }

  removeWebsite = (id: string) => {
    this._websites.delete(id)
  }

  removeEmail = (id: string) => {
    this._emails.delete(id)
  }

  setNumberLabel = (id: string, label: string) => {
    const number = this._numbers.get(id)
    if (!number) return

    number.label = label
    if (number.labelError) number.labelError = ''
  }

  setWebsiteLabel = (id: string, label: string) => {
    const website = this._websites.get(id)
    if (!website) return

    website.label = label
    if (website.labelError) website.labelError = ''
  }

  setWebsiteUrl = (id: string, url: string) => {
    const website = this._websites.get(id)
    if (!website) return

    website.url = url
    if (website.urlError) website.urlError = ''
  }

  setEmailLabel = (id: string, label: string) => {
    const email = this._emails.get(id)
    if (!email) return

    email.label = label
    if (email.labelError) email.labelError = ''
  }

  setEmailValue = (id: string, value: string) => {
    const email = this._emails.get(id)
    if (!email) return

    email.email = value
    if (email.emailError) email.emailError = ''
  }

  validate = () => {
    let hasError = false

    if (this.hasNumbers) {
      this.numbers.forEach((number) => {
        if (!number.label) {
          number.labelError = 'Enter a label'
          hasError = true
        }

        number.phoneInputStore.triggerNumberValidation()
        if (number.phoneInputStore.numberError) hasError = true
      })
    }

    if (this.hasWebsites) {
      this.websites.forEach((website) => {
        if (!website.label) {
          website.labelError = 'Enter a label'
          hasError = true
        }

        if (!website.url || !isLink(website.url)) {
          website.urlError = 'Enter a valid URL'
          hasError = true
        }
      })
    }

    if (this.hasEmails) {
      this.emails.forEach((email) => {
        if (!email.label) {
          email.labelError = 'Enter a label'
          hasError = true
        }

        if (!email.email || !isEmail(email.email)) {
          email.emailError = 'Enter a valid email address'
          hasError = true
        }
      })
    }

    return hasError
  }

  init = (data: IResponseRcs) => {
    this._numbers = new Map(
      data.contact_phones?.map((phone) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            phoneInputStore: new PhoneInputStore({
              excludeCountries: [CountryEnum.GB, CountryEnum.AU],
              number: phone.phone_number,
            }),
            label: phone.label,
            labelError: '',
          },
        ]
      })
    )
    this._websites = new Map(
      data.website_urls?.map((website) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            url: website.website,
            urlError: '',
            label: website.label,
            labelError: '',
          },
        ]
      })
    )
    this._emails = new Map(
      data.contact_emails?.map((email) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            email: email.email,
            emailError: '',
            label: email.label,
            labelError: '',
          },
        ]
      })
    )

    if (!this._numbers.size) this.addNumber()
  }

  initErrors = (errors: INormalizedApiError) => {
    this.numbers.forEach((value, index) => {
      const number = this._numbers.get(value.id)
      if (!number) return
      number.labelError = errors.getError(`contact_phones.${index}.label`)
      number.phoneInputStore.setNumberError(errors.getError(`contact_phones.${index}.phone_number`))
    })
    this.emails.forEach((value, index) => {
      const email = this._emails.get(value.id)
      if (!email) return
      email.labelError = errors.getError(`contact_emails.${index}.label`)
      email.emailError = errors.getError(`contact_emails.${index}.email`)
    })
    this.websites.forEach((value, index) => {
      const website = this._websites.get(value.id)
      if (!website) return
      website.labelError = errors.getError(`website_urls.${index}.label`)
      website.urlError = errors.getError(`website_urls.${index}.website`)
    })
  }

  reset = () => {
    this._numbers = new Map()
    this._websites = new Map()
    this._emails = new Map()
    this.addNumber()
  }
}
